import type { PrepareModulesReport } from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'

/**
 * 배치 준비 강제 정리 — `POST /modules/prepare`(`prepareModulesForBatch` · 선행 AH-26).
 *
 * ## 무엇을 하는가
 *
 * **한 번의 호출로 「배치를 시작할 수 있는 상태」로 되돌린다.** 흩어져 있던 정리 경로 넷을
 * 순서대로 «실행»한다(계약 summary · `app/services/module_prepare.py`):
 *
 * 1. **비종착 배치를 전부 종착시킨다** — `running` 은 `stop`, `paused` 는 `stop` 을 받지 않아
 *    `recoverBatch discard` 다. ⚠ **`created`(아직 시작도 안 한 배치)도 든다**(`STOPPABLE`).
 * 2. 자원 락·격리는 ①에 딸려 풀린다(별도 해제 경로를 두지 않는다 · 실측 2026-09-21)
 * 3. 모듈을 유휴로 — `cancel → safety_clear → resume → recovery_reset → initialize`
 *    (**순서가 강제다**: `/resume` 은 `safety_stop` 을 풀지 않고 409 를 낸다)
 * 4. 종착 배치가 남긴 핸드오프 점유를 모듈 `/handoff/confirm` 으로 푼다
 *
 * ## 🔴 되돌릴 수 없다
 *
 * 닫힌 배치는 다시 열리지 않고 **그 시료는 재사용할 수 없다**(히나 실측 2026-09-22:
 * 「방금 만든 배치도 닫히고 그 시료는 재사용 불가가 된다」). 그래서 본문이
 * `IrreversibleCommandEnvelope` 이고 **`reason` 이 필수**다 — 감사 원장에 그대로 봉인된다.
 * 🔴 **사유를 프론트가 합성하지 않는다.** 사람이 적은 것만 보낸다(원장이 거짓이 되면 안 된다).
 *
 * ## ⚠ 게이트 둘
 *
 * | 축 | 값 | 근거 |
 * | --- | --- | --- |
 * | 역할 | operator · admin | `views/modules.py:1479` `require_roles(WRITE_ROLES)` |
 * | 제어락 | **필수** | 같은 줄 `require_control_lock` · `X-Control-Token` |
 *
 * 제어락 헤더는 전송 계층이 붙인다 — `CONTROL_TOKEN_ROUTES` 에 이 경로가 **등재되어 있어야**
 * 하고(2026-09-22 에 더했다), 빠지면 헤더 없이 나가 **409** 다.
 *
 * ⚠ **`ready: false` 도 200 이다.** 실패가 아니라 「무엇이 남았는지」가 본문에 있다 —
 *   화면이 그것을 오류로 접으면 사람이 다음에 할 일을 잃는다(계약 원문).
 */
export interface PrepareModulesVars {
  /** 🔴 사람이 적은 사유. 감사 원장에 그대로 들어간다 */
  reason: string
  commandId?: string
}

export const usePrepareModules = createCommand<PrepareModulesVars, PrepareModulesReport>({
  send: ({ reason, commandId }) =>
    orchestrator.request<PrepareModulesReport>({
      method: 'POST',
      path: '/modules/prepare',
      body: { command_id: commandId ?? newCommandId(), reason },
      timeoutMs: REQUEST_TIMEOUT_MS.prepare,
      retryOn401: true,
    }),
  /*
   * 🔴 **이 연산이 건드리는 도메인 전부를 비운다.** 배치(종착) · 모듈(상태) · 배치 추적(트레이 위치 ·
   *    종착) · 안전(해제 fan-out) 넷이다. 하나라도 빠지면 화면이 옛 값을 이고 있는다.
   *    ⚠ 자원 점유(`resource-locks`)는 서버 v0.9.4 에서 걷혔다 — 그 자리를 배치 추적이 이었다.
   * ⚠ 무효화해도 **활성 관찰자가 없는 쿼리는 네트워크를 타지 않는다** — 정상이다.
   */
  invalidates: () => [
    keys.batches.all(),
    keys.modules.all(),
    keys.tracking.all(),
    keys.safety.all(),
  ],
})
